import React, { useContext, useEffect, useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { ChechboxWithPermissions } from '@vlabs/shared/components/checkbox-with-permissions/CheckboxWithPermissions';
import { permissions } from '@vlabs/shared/config';
import { selectIsAppFeatureAvailable } from '@vlabs/shared/selectors/appSelectors';
import { Tabs, Tab, useModal } from '@vlabs/uikit';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { DefaultFaceFormFields } from './DefaultFaceFormFields';
import { FaceCreateContext } from './FaceCreateFormContext';
import st from './IsoQualityCheckForm.module.sass';
import { IsoErrorsTab } from '../components/IsoErrorsTab';
import type { IsoQualityCheckFormProps } from '../types';

function IsoQualityCheckFormComponent({
  isFaceQualityAvailable,
  noSelect,
}: IsoQualityCheckFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const isoResultModal = useModal();

  const { control } = useFormContext();
  const file = useWatch({ name: 'file', control });

  const {
    sampleId, detections, isoResult, setIsoResult, shouldCheckISO, setShouldCheckISO,
  } = useContext(FaceCreateContext);

  const fetchIsoResults = async (imageFile: File) => {
    const { images, raw } = await apiContainer.lunaClient.iso.check([imageFile]);
    setIsoResult({ data: images[0], raw, status: images[0]?.estimations[0].face?.detection?.iso?.status });
  };

  useEffect(() => {
    if (isoResult?.status === 0 && shouldCheckISO) isoResultModal.open();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoResult]);

  useEffect(() => {
    if (!file || !shouldCheckISO || sampleId?.length === 0) {
      setIsoResult(null);
      setActiveTab(0);
      return;
    }

    async function run() {
      if (detections.length > 1) {
        const img = await apiContainer.lunaClient.samples.getImage('faces', ...sampleId);
        fetchIsoResults(img);
      } else {
        fetchIsoResults(file.raw);
      }
    }
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, sampleId, shouldCheckISO, detections]);

  return (
    <Tabs
      activeTabIndex={activeTab}
      noHeaders
    >
      <Tab
        data-testid="iso.info"
        name="iso info"
      >
        {!!isoResult?.data && isoResult?.status === 0 && isoResultModal.wrap(
          <IsoErrorsTab results={isoResult.data?.estimations[0]?.face} />,
        )}
        <div className={st.IsoInfo}>
          <DefaultFaceFormFields noSelect={noSelect} />
          {!!isFaceQualityAvailable && (
          <div className={st.Switch}>
            <ChechboxWithPermissions
              disabled={!file}
              onChange={() => setShouldCheckISO((prev: boolean) => !prev)}
              permissions={{ rules: [permissions.resources.iso] }}
              value={shouldCheckISO}
            />
            <div className={st.Switch__Label}>
              {t('monokit:create face modal.iso.check iso quality label')}
            </div>
          </div>
          )}
        </div>
      </Tab>
    </Tabs>
  );
}

export const IsoQualityCheckForm = connect(
  (state) => ({
    isFaceQualityAvailable: selectIsAppFeatureAvailable(state, 'estimations.face_quality'),
  }),
)(IsoQualityCheckFormComponent);
